import React, { useState } from "react";
import ProviderInput from "./components/ProviderInput";
import DragDropUpload from "./components/DragDropUpload";
import RadioYesNo from "./components/RadioYesNo";
import MultiFieldList from "./components/MultiFieldList";
import MultiContactList from "./components/MultiContactList";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import { ReactComponent as CollapseIcon } from "../../../assets/icons/financialPromotions/expand-arrow.svg";
import { ReactComponent as AddIcon } from "../../../assets/icons/add.svg";


const PlanPolicyForm = ({
  plan,
  index,
  onChange,
  onRemove,
  canRemove = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const planTitle = plan.planName?.trim()
    ? plan.planName
    : `Plan/Policy ${index + 1}`;

  const updateField = (field, value) => {
    onChange(index, {
      ...plan,
      [field]: value,
    });
  };

  const updateGuideline = (field, value) => {
    onChange(index, {
      ...plan,
      loaGuidelines: {
        ...plan.loaGuidelines,
        [field]: value,
      },
    });
  };

  const updateAttachments = (files) => {
    onChange(index, {
      ...plan,
      attachmentFiles: Array.from(files || []),
    });
  };

  const handleDelete = () => {
    if (!canRemove) return;

    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${planTitle}"?`,
    );

    if (!confirmDelete) return;

    onRemove(index);
  };

  const toggleOpen = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <section className="provider-network-form-card provider-network-plan-card">
      <div className="provider-network-plan-header">
        <button
          type="button"
          className="provider-network-plan-title-btn"
          onClick={toggleOpen}
        >
          <span className="FP_Subtitle">Plan/Policy Details</span>

          {plan.planName?.trim() && (
            <span className="provider-network-plan-title">{planTitle}</span>
          )}
        </button>

        <div className="provider-network-plan-actions">
          {canRemove && (
            <button
              type="button"
              className="provider-network-icon-btn provider-network-delete-plan-btn"
              onClick={handleDelete}
              title="Delete plan/policy"
              aria-label="Delete plan/policy"
            >
              <DeleteIcon />
            </button>
          )}

          <button
            type="button"
            className={`provider-network-icon-btn provider-network-collapse-btn ${
              isOpen ? "is-open" : ""
            }`}
            onClick={toggleOpen}
            title={isOpen ? "Collapse section" : "Expand section"}
            aria-label={isOpen ? "Collapse section" : "Expand section"}
          >
            <CollapseIcon className="provider-network-collapse-icon" />
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="provider-network-plan-body">
          <ProviderInput
            label="Plan/Policy Name"
            required
            value={plan.planName || ""}
            placeholder="Name of Plan/Policy"
            onChange={(value) => updateField("planName", value)}
          />

          <ProviderInput
            label="Plan Type"
            value={plan.planType || ""}
            placeholder="Plan Type"
            onChange={(value) => updateField("planType", value)}
          />

          <ProviderInput
            label="Policy Starting With"
            value={plan.policyStartingWith || ""}
            placeholder="Policy Starting With"
            onChange={(value) => updateField("policyStartingWith", value)}
          />

          <MultiFieldList
            label="Address"
            values={plan.addresses || [plan.address || ""]}
            placeholder="Full Address including Street, Town/City and Postcode"
            addLabel="Add Address"
            onChange={(values) => updateField("addresses", values)}
          />

          <MultiContactList
            label="Contact"
            contacts={
              plan.contacts || [
                plan.contact || { countryCode: "+44", number: "" },
              ]
            }
            onChange={(values) => updateField("contacts", values)}
          />

          <MultiFieldList
            label="Email"
            type="email"
            values={plan.emails || [plan.email || ""]}
            placeholder="Email Address"
            addLabel="Add Email"
            onChange={(values) => updateField("emails", values)}
          />

          <MultiFieldList
            label="Website / Online Portal"
            values={plan.websites || [plan.website || ""]}
            placeholder="Website / Online Portal Link"
            addLabel="Add Website"
            onChange={(values) => updateField("websites", values)}
          />

          <div className="FP_Body_Semibold provider-network-section-title">
            LOA Guidelines
          </div>

          <div className="provider-network-guidelines-grid">
            <RadioYesNo
              label="Accepts scanned docs"
              value={plan.loaGuidelines?.acceptsScannedDocs}
              onChange={(value) => updateGuideline("acceptsScannedDocs", value)}
            />

            <RadioYesNo
              label="Accepts LOAs by post only"
              value={plan.loaGuidelines?.acceptsLoaByPostOnly}
              onChange={(value) =>
                updateGuideline("acceptsLoaByPostOnly", value)
              }
            />

            <RadioYesNo
              label="Accepts digitally signed docs"
              value={plan.loaGuidelines?.acceptsDigitallySignedDocs}
              onChange={(value) =>
                updateGuideline("acceptsDigitallySignedDocs", value)
              }
            />

            <RadioYesNo
              label="Accepts wet signature only"
              value={plan.loaGuidelines?.acceptsWetSignatureOnly}
              onChange={(value) =>
                updateGuideline("acceptsWetSignatureOnly", value)
              }
            />
            <RadioYesNo
              label="Accepts Unipass letter of authority"
              value={plan.loaGuidelines?.acceptsLoaInsteadOfAuthority}
              onChange={(value) =>
                updateGuideline("acceptsLoaInsteadOfAuthority", value)
              }
            />

            <RadioYesNo
              label="Secure Email"
              value={plan.loaGuidelines?.secureEmail}
              onChange={(value) => updateGuideline("secureEmail", value)}
            />
          </div>

          <ProviderInput
            label="Additional Notes"
            type="textarea"
            value={plan.notes || ""}
            placeholder="Enter any additional information here"
            onChange={(value) => updateField("notes", value)}
          />

          <DragDropUpload
            label="Attachments"
            multiple
            files={plan.attachmentFiles || []}
            onChange={updateAttachments}
            accept=".pdf,.doc,.docx,.csv,.txt,.png,.jpg,.jpeg"
            acceptedText="Accepted file types: pdf, doc, docx, csv, txt, png, jpg, jpeg"
          />
        </div>
      )}
    </section>
  );
};

export default PlanPolicyForm;
